import {Button} from '@/components/ui/button/Button'
import {
    DialogDisclosure,
    DialogProvider,
    Dialog,
    DialogDismiss,
    DialogBody,
    DialogFooter
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {PencilLineIcon} from '@/components/ui/icon/Icon'
import {useTranslation} from '@/translations/i18n'
import {ProjectExtended} from '@/features/projects/types'
import {
    ProjectDetailsDialogHeader,
    ProjectDetailsDialogHeading
} from '@/features/projects/components/project-details-dialog-atoms/ProjectDetailsDialogAtoms'
import {EditProjectTeamFormProvider} from '@/features/projects/components/project-form/edit-project-team-form-provider/EditProjectTeamFormProvider'
import {useMemo} from 'react'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {StyledProjectDetailsDialogContent} from '@/features/projects/components/project-team-edit-dialog/style'
import {EditProjectTeamFormFooter} from '@/features/projects/components/project-form/edit-project-team-form-footer/EditProjectTeamFormFooter'
import {useDialogContext} from '@ariakit/react'
import {EditProjectTeamFormFields} from '@/features/projects/components/project-form/edit-project-team-form-fields/EditProjectTeamFormFields'

interface ProjectTeamEditDialogProps {
    project: ProjectExtended
    isAReserve?: boolean
}

export const ProjectTeamEditDialog = ({project, isAReserve}: ProjectTeamEditDialogProps) => {
    const {t} = useTranslation()
    return (
        <DialogProvider>
            <DialogDisclosure
                render={
                    <Button variant="ghost">
                        <PencilLineIcon />
                        {t('commons:edit')}
                    </Button>
                }
            />
            <Dialog width="800px" overflowY="unset">
                <DialogContent project={project} isAReserve={isAReserve} />
            </Dialog>
        </DialogProvider>
    )
}

const DialogContent = ({project, isAReserve}: {project: ProjectExtended; isAReserve?: boolean}) => {
    const {t} = useTranslation()
    const dialogContext = useDialogContext()

    const reserveTeam = useMemo(() => {
        return project.team.filter(member => member.isAReserve)
    }, [project])

    const regularTeam = useMemo(() => {
        return project.team.filter(member => !member.isAReserve)
    }, [project])

    const projectEquipeToDefaultValues = useMemo(() => {
        const teamToUse = isAReserve ? reserveTeam : regularTeam

        // Check if we have a referring_doctor in the team
        const hasReferringDoctor = teamToUse.some(member => member.group == 'referring_doctor')

        // Create a new team array
        let formattedTeam = [...teamToUse]

        // If no referring_doctor is present, add an empty field at the beginning
        if (!hasReferringDoctor && !isAReserve) {
            const emptyReferringDoctor = {
                id: '',
                fullName: '',
                specializationName: '',
                isAReserve: !!isAReserve,
                group: 'referring_doctor' as const
            }

            formattedTeam = [emptyReferringDoctor, ...formattedTeam]
        }

        return {
            id: project.id,
            team: formattedTeam,
            treatmentRequestId: project.treatmentRequestId
        }
    }, [project, isAReserve, reserveTeam, regularTeam])

    return (
        <StyledProjectDetailsDialogContent>
            <ProjectDetailsDialogHeader>
                <ProjectDetailsDialogHeading>{t('projects:edit_rehabilitation_team')}</ProjectDetailsDialogHeading>
                <DialogDismiss />
            </ProjectDetailsDialogHeader>
            <EditProjectTeamFormProvider defaultValues={projectEquipeToDefaultValues}>
                <ScrollArea style={{height: '100%'}} scrollbar={<Scrollbar />}>
                    <DialogBody>
                        <EditProjectTeamFormFields isAReserve={isAReserve} />
                    </DialogBody>
                </ScrollArea>
                <DialogFooter>
                    <EditProjectTeamFormFooter
                        isAReserve={isAReserve}
                        patientId={project.patientId}
                        onCancel={() => dialogContext?.setOpen(false)}
                    />
                </DialogFooter>
            </EditProjectTeamFormProvider>
        </StyledProjectDetailsDialogContent>
    )
}
